import { createContext, useContext } from 'react';

/**
 * Where the shop's own addresses are, for a page that does not know which shop
 * it is inside.
 *
 * Every storefront page used to write its links out longhand - `/store/cart`,
 * `` `/store/products/${slug}` `` - which was correct for as long as there was
 * exactly one place a storefront could be mounted. There are two now: the
 * merchant's live shop at `/store`, and a theme's static preview at
 * `/themes/<id>`, which is a complete shop of its own built from a theme's
 * mock catalog. The same catalog page, the same cart, the same product page
 * render in both, and a hardcoded `/store` in any of them is a link that walks
 * a merchant out of the preview they were exploring.
 *
 * So the base is a value, and the addresses are built from it. One object,
 * because the alternative - a `join(base, path)` helper at seventy call sites
 * - puts the shape of every URL back in the call sites, which is where it goes
 * out of step. Adding a page to the storefront is a method here, and both
 * hosts get it.
 */
export interface StorefrontPaths {
  /** The shop's root: `/store`, or `/themes/<id>`. Never a trailing slash. */
  readonly base: string;

  home(): string;

  /** The catalog, optionally narrowed to a shelf by slug. */
  products(categorySlug?: string | null): string;
  /** The catalog with a query string this page has already assembled. */
  productsQuery(query: string | URLSearchParams): string;
  product(slug: string): string;

  /** The department index, and one department's own address. */
  categories(): string;
  category(slug: string): string;

  search(query?: string | URLSearchParams): string;

  cart(): string;
  /** The shopper's saved products (the wishlist plugin). */
  wishlist(): string;
  checkout(): string;
  /** Where an order lands once it is placed. */
  order(orderNumber: string): string;

  page(handle: string): string;
  /** A NeonCarts policy shown in the shop's chrome, for a footer link. */
  policy(slug: string): string;
  blog(handle: string, query?: string | URLSearchParams): string;
  post(blogHandle: string, postHandle: string): string;
  /** The open positions, and one opening's own page. */
  careers(): string;
  career(slug: string): string;

  account(): string;
  accountOrders(): string;
  accountOrder(orderId: string): string;
  accountBookings(): string;
  accountPackages(): string;
  accountAddresses(): string;
  accountProfile(): string;
  accountLogin(): string;
  accountRegister(): string;
  accountForgotPassword(): string;
  accountResetPassword(): string;
  accountVerifyEmail(): string;

  /**
   * A path the merchant typed into a menu, made absolute against this shop.
   *
   * Menu links are stored the way a shopper reads them - `/cart`, `/products`
   * - because a merchant should not have to know which prefix their shop is
   * served under. This is where that becomes true.
   */
  menu(url: string): string;
}

/** A query string from either of the two shapes call sites hold one in. */
function qs(query: string | URLSearchParams | undefined): string {
  if (query === undefined) return '';
  const value = typeof query === 'string' ? query : query.toString();
  if (!value) return '';
  return value.startsWith('?') ? value : `?${value}`;
}

/**
 * The addresses of a storefront mounted at `base`.
 *
 * A plain function rather than a hook, so the modules that build links outside
 * a component - a facet panel's "clear this filter" href, a category link
 * shared by the header and the footer - can take the paths as an argument
 * instead of becoming components to reach a context.
 */
export function storefrontPaths(base: string): StorefrontPaths {
  // A base with a trailing slash would double every separator below, and a
  // base that is bare `/` would leave every path starting `//` - which the
  // browser reads as a protocol-relative host, not a path on this site.
  const root = base.replace(/\/+$/, '');
  const at = (path: string) => `${root}${path}`;

  return {
    base: root,
    home: () => root || '/',
    products: (categorySlug) =>
      categorySlug ? at(`/products?category=${encodeURIComponent(categorySlug)}`) : at('/products'),
    productsQuery: (query) => at(`/products${qs(query)}`),
    product: (slug) => at(`/products/${encodeURIComponent(slug)}`),
    categories: () => at('/categories'),
    category: (slug) => at(`/categories/${encodeURIComponent(slug)}`),
    search: (query) => at(`/search${qs(query)}`),
    cart: () => at('/cart'),
    wishlist: () => at('/wishlist'),
    checkout: () => at('/checkout'),
    order: (orderNumber) => at(`/order/${encodeURIComponent(orderNumber)}`),
    page: (handle) => at(`/pages/${encodeURIComponent(handle)}`),
    policy: (slug) => at(`/policies/${encodeURIComponent(slug)}`),
    blog: (handle, query) => at(`/blogs/${encodeURIComponent(handle)}${qs(query)}`),
    post: (blogHandle, postHandle) =>
      at(`/blogs/${encodeURIComponent(blogHandle)}/${encodeURIComponent(postHandle)}`),
    careers: () => at('/careers'),
    career: (slug) => at(`/careers/${encodeURIComponent(slug)}`),
    account: () => at('/account'),
    accountOrders: () => at('/account/orders'),
    accountOrder: (orderId) => at(`/account/orders/${encodeURIComponent(orderId)}`),
    accountBookings: () => at('/account/bookings'),
    accountPackages: () => at('/account/packages'),
    accountAddresses: () => at('/account/addresses'),
    accountProfile: () => at('/account/profile'),
    accountLogin: () => at('/account/login'),
    accountRegister: () => at('/account/register'),
    accountForgotPassword: () => at('/account/forgot-password'),
    accountResetPassword: () => at('/account/reset-password'),
    accountVerifyEmail: () => at('/account/verify-email'),
    menu: (url) => {
      const external = /^(https?:)?\/\//.test(url) || url.startsWith('mailto:');
      if (external) return url;
      return url === '/' ? root || '/' : at(url.startsWith('/') ? url : `/${url}`);
    },
  };
}

/**
 * The merchant's own shop.
 *
 * Also the context default, which is what keeps the customizer's preview
 * working: it renders storefront chrome with no provider around it, and the
 * links it produces are inert there anyway.
 */
export const LIVE_STOREFRONT_PATHS = storefrontPaths('/store');

export const StorefrontPathsContext = createContext<StorefrontPaths>(LIVE_STOREFRONT_PATHS);

/** The addresses of the shop this component is being rendered inside. */
export function useStorefrontPaths(): StorefrontPaths {
  return useContext(StorefrontPathsContext);
}
